import { useState } from 'react';
import { showDefaultErrorToast } from '@app/utils';
import {
  useAssignCategoryToQuestion,
  useCreateCategory as useCreateCategoryMutation,
  useCreateCategoryContextTranslations,
  useCategoriesInfiniteQuery,
} from '@data/hooks';
import { CategoryCreateBody, CategoryType, Language } from '@data/sdk/match';
import { AntdForm } from '@ui/components';

const initialValues: Partial<CategoryCreateBody> = {
  name: '',
  shortName: '',
  context: '',
  priority: 0,
};

export const useCreateCategory = ({
  close,
  type,
  questionId,
}: {
  close: () => void;
  questionId: string;
  type: CategoryType;
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { categoriesLoading, categoriesOptions, fetchNextPage, handleSearchChange } =
    useCategoriesInfiniteQuery(type);
  const { mutate: createCategory, isPending: isCategoryCreating } = useCreateCategoryMutation(type);
  const {
    mutate: createCategoryContextTranslations,
    isPending: isCategoryContextTranslationsCreating,
  } = useCreateCategoryContextTranslations();
  const { mutate: assignCategoryToQuestion, isPending: isCategoryAssigningToQuestion } =
    useAssignCategoryToQuestion();

  const [form] = AntdForm.useForm<CategoryCreateBody>();
  const category = AntdForm.useWatch('category', form);

  const handleScroll = (e: any) => {
    if (e.target.scrollTop + e.target.offsetHeight + 2 > e.target.scrollHeight) {
      fetchNextPage();
    }
  };

  const assignCategory = (id: string) => {
    assignCategoryToQuestion(
      { categoryId: id, id: questionId },
      {
        onSuccess: () => {
          close();
          setIsEditMode(false);
          form.resetFields();
        },
        onError: showDefaultErrorToast,
      },
    );
  };

  const handleCreateCategory = async (categoryCreateBody: CategoryCreateBody) => {
    if (category) {
      assignCategory(category);
    } else {
      const { priority, name, shortName, context } = categoryCreateBody;

      createCategory(
        {
          priority,
          type,
          name,
          ...(shortName && { shortName }),
          ...(context && { context }),
        },
        {
          onSuccess: ({ id }) => {
            assignCategory(id);

            if (context) {
              createCategoryContextTranslations({
                id,
                categoryContextTranslationsBody: {
                  translations: Object.values(Language).map((language) => ({
                    language,
                    value: context,
                    autoGenerate: language !== Language.En,
                  })),
                },
              });
            }
          },
          onError: showDefaultErrorToast,
        },
      );
    }
  };

  return {
    form,
    isEditMode,
    categoriesLoading,
    categoriesOptions,
    initialValues,
    isLoading:
      isCategoryCreating || isCategoryAssigningToQuestion || isCategoryContextTranslationsCreating,
    handleScroll,
    setIsEditMode,
    handleSearchChange,
    handleCreateCategory,
  };
};
